import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowRight } from "lucide-react";
import { IMAGES } from "@/lib/site";

const HEADLINE = [
  "Better data.",
  "Smarter marketing.",
  "More customers.",
];

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.15 } },
};
const lineMask = {
  hidden: { y: "110%" },
  show: {
    y: "0%",
    transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] },
  },
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "24%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      ref={ref}
      data-testid="hero"
      className="relative min-h-[100svh] flex items-end overflow-hidden rsm-grid-bg"
    >
      {/* Parallax ambient image */}
      <motion.div
        style={{ y: imgY, scale: imgScale }}
        className="absolute inset-0 z-0"
      >
        <img
          src={IMAGES.hero}
          alt="Abstract data network visualization in black and gold"
          className="h-full w-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0d14] via-[#0a0d14]/70 to-[#0a0d14]/30" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_70%_20%,rgba(245,158,11,0.12),transparent_55%)]" />
      </motion.div>

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 lg:pb-24 pt-32"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="rsm-eyebrow mb-6 flex items-center gap-3"
        >
          <span className="h-px w-8 bg-amber-400/60" />
          Data · Paid Acquisition · Automation
        </motion.div>

        <motion.h1
          variants={container}
          initial="hidden"
          animate="show"
          className="font-display text-4xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[1.05] text-white max-w-5xl"
        >
          {HEADLINE.map((line, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span
                variants={lineMask}
                className="block"
              >
                {i === 2 ? (
                  <span className="text-amber-400">{line}</span>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7 }}
          className="mt-8 max-w-xl text-base sm:text-lg leading-relaxed text-slate-300"
        >
          We combine targeted data, paid media, email marketing, automation,
          and sales strategy to build customer acquisition systems that
          actually convert.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.85 }}
          className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4"
        >
          <a
            href="/#services"
            data-testid="hero-cta-primary"
            className="group inline-flex items-center gap-2 rounded-full bg-amber-500 px-7 py-3.5 text-sm font-semibold text-[#0a0d14] hover:bg-amber-400 transition-colors"
          >
            Explore Services
            <ArrowRight
              size={17}
              className="transition-transform group-hover:translate-x-1"
            />
          </a>
          <a
            href="/#contact"
            data-testid="hero-cta-secondary"
            className="inline-flex items-center gap-2 rounded-full border border-white/15 px-7 py-3.5 text-sm font-semibold text-white hover:border-amber-400/50 hover:text-amber-400 transition-colors"
          >
            Get a Data Strategy
          </a>
        </motion.div>
      </motion.div>
    </section>
  );
}
